import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  AdminViewToggle,
  useAdminViewMode,
} from "@jambushrushti/components/admin/AdminViewToggle";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import { Switch } from "@jambushrushti/components/ui/switch";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@jambushrushti/components/ui/dialog";
import { useToast } from "@jambushrushti/hooks/use-toast";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Eye,
  EyeOff,
} from "lucide-react";
import { format, isValid } from "date-fns";
import { cmsFetch, getAdminSession } from "@jambushrushti/services/adminAuth";
import {
  IMAGE_ACCEPT,
  toMultipart,
  validateImageFile,
} from "@jambushrushti/lib/multipart";

interface Blog {
  id: string;
  title: string;
  excerpt: string | null;
  content: string | null;
  author: string | null;
  cover_image: string | null;
  is_published: boolean | null;
  created_at: string;
}

const BLOG_API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");
const BLOG_ENDPOINT = `${BLOG_API_BASE}/jambushrusti/cms/blog-posts/`;
const blogHeaders = (): HeadersInit => {
  const token = getAdminSession()?.accessToken;
  return {
    "Content-Type": "application/json",
    "X-Tenant-ID":
      import.meta.env.VITE_X_TENANTID || "6cf833d7-bf6f-4688-9136-5674d8053b85",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function parseBlogResponse(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      payload?.detail ||
      payload?.message ||
      payload?.error?.message ||
      payload?.error;
    throw new Error(
      typeof message === "string" ? message : "Blog request failed."
    );
  }
  return payload;
}

const normalizeBlog = (record: Record<string, any>): Blog => ({
  id: String(record.id || record.uuid || ""),
  title: String(record.title || ""),
  excerpt: record.excerpt || null,
  content: record.content || null,
  author: record.author || null,
  cover_image: record.cover_image || record.cover_image_url || null,
  is_published: Boolean(record.is_published),
  created_at: record.created_at || "",
});

export default function BlogManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useAdminViewMode();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    author: "",
    is_published: true,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: blogs = [], isLoading } = useQuery({
    queryKey: ["admin-blogs"],
    queryFn: async () => {
      const payload = await parseBlogResponse(
        await cmsFetch(BLOG_ENDPOINT, { method: "GET" })
      );
      const rows =
        payload?.results || payload?.data?.results || payload?.data || payload;
      return (Array.isArray(rows) ? rows : []).map(normalizeBlog);
    },
  });

  const toBlogPayload = (data: typeof formData) => ({
    title: data.title,
    excerpt: data.excerpt,
    content: data.content,
    author: data.author,
    is_published: data.is_published,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return parseBlogResponse(
        await cmsFetch(BLOG_ENDPOINT, {
          method: "POST",
          body: toMultipart(toBlogPayload(data), "cover_image", coverImage),
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blogs"] });
      queryClient.invalidateQueries({ queryKey: ["blogs-public"] });
      toast({ title: "Blog post added successfully!" });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding blog post",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      return parseBlogResponse(
        await cmsFetch(`${BLOG_ENDPOINT}${id}/`, {
          method: "PATCH",
          body: toMultipart(toBlogPayload(data), "cover_image", coverImage),
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blogs"] });
      queryClient.invalidateQueries({ queryKey: ["blogs-public"] });
      toast({ title: "Blog post updated successfully!" });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating blog post",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return parseBlogResponse(
        await cmsFetch(`${BLOG_ENDPOINT}${id}/`, {
          method: "DELETE",
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blogs"] });
      queryClient.invalidateQueries({ queryKey: ["blogs-public"] });
      toast({ title: "Blog post deleted successfully!" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting blog post",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      excerpt: "",
      content: "",
      author: "",
      is_published: true,
    });
    setEditingBlog(null);
    setCoverImage(null);
  };

  const handleEdit = (blog: Blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      excerpt: blog.excerpt || "",
      content: blog.content || "",
      author: blog.author || "",
      is_published: blog.is_published ?? true,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    if (editingBlog) {
      updateMutation.mutate({ id: editingBlog.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const filteredBlogs = blogs.filter(blog =>
    blog.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Blog Management
            </h1>
            <p className="mt-1 text-muted-foreground">
              Create and manage blog posts
            </p>
          </div>
          <Dialog
            open={isDialogOpen}
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="w-fit gap-2 md:w-auto">
                <Plus className="h-4 w-4" />
                Add Blog Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingBlog ? "Edit Blog Post" : "Add New Blog Post"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4">
                  <div>
                    <Label>Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={e =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="Blog post title"
                    />
                  </div>
                  <div>
                    <Label>Author</Label>
                    <Input
                      value={formData.author}
                      onChange={e =>
                        setFormData({ ...formData, author: e.target.value })
                      }
                      placeholder="Author name"
                    />
                  </div>
                  <div>
                    <Label>Cover Image</Label>
                    <Input
                      type="file"
                      accept={IMAGE_ACCEPT}
                      onChange={e => {
                        const file = e.target.files?.[0] || null;
                        if (!file) {
                          return setCoverImage(null);
                        }
                        try {
                          validateImageFile(file);
                          setCoverImage(file);
                        } catch (error) {
                          e.target.value = "";
                          toast({
                            title: (error as Error).message,
                            variant: "destructive",
                          });
                        }
                      }}
                    />
                    {editingBlog?.cover_image && !coverImage && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Keep the current image, or choose a replacement.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Excerpt</Label>
                    <Textarea
                      value={formData.excerpt}
                      onChange={e =>
                        setFormData({ ...formData, excerpt: e.target.value })
                      }
                      placeholder="Short summary..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Content</Label>
                    <Textarea
                      value={formData.content}
                      onChange={e =>
                        setFormData({ ...formData, content: e.target.value })
                      }
                      placeholder="Full blog content..."
                      rows={8}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_published}
                      onCheckedChange={checked =>
                        setFormData({ ...formData, is_published: checked })
                      }
                    />
                    <Label>Published (visible on website)</Label>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                  >
                    {(createMutation.isPending || updateMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {editingBlog ? "Update Post" : "Add Post"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 gap-y-3">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search blog posts..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <AdminViewToggle value={viewMode} onChange={setViewMode} />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div
            className={
              viewMode === "cards"
                ? "grid gap-4 md:grid-cols-2 xl:grid-cols-3"
                : "grid gap-4"
            }
          >
            {filteredBlogs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    No blog posts found. Create your first post!
                  </p>
                </CardContent>
              </Card>
            ) : viewMode === "table" ? (
              <div className="overflow-x-auto rounded-xl border bg-card md:col-span-2 xl:col-span-3">
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-muted/60 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Author</th>
                      <th className="px-4 py-3">Created</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredBlogs.map(blog => (
                      <tr key={blog.id} className="hover:bg-muted/30">
                        <td className="max-w-sm px-4 py-3 font-medium">
                          <span className="line-clamp-1">{blog.title}</span>
                        </td>
                        <td className="px-4 py-3">{blog.author || "—"}</td>
                        <td className="px-4 py-3">
                          {blog.created_at && isValid(new Date(blog.created_at))
                            ? format(new Date(blog.created_at), "MMM d, yyyy")
                            : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              blog.is_published ? "default" : "secondary"
                            }
                          >
                            {blog.is_published ? "Published" : "Draft"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(blog)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() =>
                                confirm(
                                  "Are you sure you want to delete this blog post?"
                                ) && deleteMutation.mutate(blog.id)
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              filteredBlogs.map(blog => (
                <Card key={blog.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {blog.cover_image && (
                        <img
                          src={blog.cover_image}
                          alt={blog.title}
                          className="h-24 w-24 shrink-0 rounded-lg object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <h3 className="line-clamp-1 font-semibold text-foreground">
                              {blog.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {blog.author || "Unknown author"} •{" "}
                              {blog.created_at &&
                              isValid(new Date(blog.created_at))
                                ? format(
                                    new Date(blog.created_at),
                                    "MMM d, yyyy"
                                  )
                                : "-"}
                            </p>
                          </div>
                          <Badge
                            className="w-fit"
                            variant={
                              blog.is_published ? "default" : "secondary"
                            }
                          >
                            {blog.is_published ? (
                              <Eye className="mr-1 h-3 w-3" />
                            ) : (
                              <EyeOff className="mr-1 h-3 w-3" />
                            )}
                            {blog.is_published ? "Published" : "Draft"}
                          </Badge>
                        </div>
                        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                          {blog.excerpt}
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(blog)}
                          >
                            <Pencil className="mr-1 h-3 w-3" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-destructive/40 text-destructive hover:border-destructive hover:bg-destructive hover:text-white"
                            onClick={() => {
                              if (
                                confirm(
                                  "Are you sure you want to delete this blog post?"
                                )
                              ) {
                                deleteMutation.mutate(blog.id);
                              }
                            }}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
